'use client';

import { Briefcase, Building2, MapPin } from 'lucide-react';
import type { ReactNode } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '../../components/avatar';
import { cn } from '../../utils/cn';
import {
  isProfilePreviewSampleAvatarPath,
  SampleProfileAvatar,
} from '../sample-profile-avatar';
import type {
  PublicProfileHeaderStyle,
  PublicProfileLayout,
} from '../public-profile-layout';

const HEADER_PLACEHOLDER_BG =
  'radial-gradient(60% 95% at 18% 12%, rgba(96,165,250,0.95), rgba(96,165,250,0) 60%), radial-gradient(55% 85% at 88% 22%, rgba(167,139,250,0.9), rgba(167,139,250,0) 55%), radial-gradient(85% 95% at 60% 115%, rgba(45,212,191,0.75), rgba(45,212,191,0) 60%), linear-gradient(135deg, #1e293b, #0f172a)';

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export type ProfileHeaderIdentity = {
  displayName: string;
  jobTitle?: string | null;
  company?: string | null;
  location?: string | null;
  showProfileImage?: boolean;
  photoPath?: string | null;
};

type ProfileHeaderProps = {
  layout: PublicProfileLayout;
  headerStyle: PublicProfileHeaderStyle;
  headerImageUrl?: string | null;
  identity: ProfileHeaderIdentity;
  resolveFileUrl: (path: string) => string;
  /** Optional CTAs rendered inside header card (cards desktop) or below identity. */
  actions?: ReactNode;
  className?: string;
};

function ProfileAvatar({
  identity,
  resolveFileUrl,
  sizeClass,
  shapeClass = 'rounded-full',
  borderClass = 'border-[3px] border-[var(--avatar-border)] sm:border-4',
  fontClass = 'text-[32px]',
  shadowClass = 'shadow-[0_12px_30px_var(--accent-16)]',
}: {
  identity: ProfileHeaderIdentity;
  resolveFileUrl: (path: string) => string;
  sizeClass: string;
  shapeClass?: string;
  borderClass?: string;
  fontClass?: string;
  shadowClass?: string;
}) {
  const photoPath = identity.photoPath?.trim();
  const showInitialsOnly = identity.showProfileImage === false || !photoPath;
  const initials = getInitials(identity.displayName);

  const shell = cn(
    'flex shrink-0 items-center justify-center font-bold text-[var(--accent-text)]',
    shadowClass,
    sizeClass,
    shapeClass,
    borderClass,
    'bg-[image:var(--accent-grad)]',
  );

  if (showInitialsOnly) {
    return (
      <div className={cn(shell, fontClass)} aria-hidden>
        {initials}
      </div>
    );
  }

  if (isProfilePreviewSampleAvatarPath(photoPath)) {
    return (
      <div className={cn(shell, 'overflow-hidden p-0')}>
        <SampleProfileAvatar className="h-full w-full" />
      </div>
    );
  }

  return (
    <Avatar
      className={cn(
        sizeClass,
        shapeClass,
        borderClass,
        'bg-[image:var(--accent-grad)]',
      )}
    >
      <AvatarImage
        src={resolveFileUrl(photoPath)}
        alt={identity.displayName}
        className="object-cover"
      />
      <AvatarFallback
        delayMs={0}
        className={cn(
          // Match parent shape (classic is rounded-square; default AvatarFallback is circular).
          shapeClass,
          'bg-[image:var(--accent-grad)] font-bold text-[var(--accent-text)]',
          fontClass,
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

function HeaderBanner({
  headerStyle,
  headerImageUrl,
  heightClass,
  radiusClass,
  scrimTo,
  scrimHeightClass,
  marginBottomClass,
}: {
  headerStyle: PublicProfileHeaderStyle;
  headerImageUrl?: string | null;
  heightClass: string;
  radiusClass?: string;
  scrimTo: string;
  scrimHeightClass: string;
  marginBottomClass?: string;
}) {
  if (headerStyle === 'minimal') return null;

  const showImage = headerStyle === 'image' && Boolean(headerImageUrl);
  const background = showImage
    ? undefined
    : headerStyle === 'gradient'
      ? 'var(--accent-grad)'
      : HEADER_PLACEHOLDER_BG;

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        heightClass,
        radiusClass,
        marginBottomClass,
      )}
      style={background ? { background } : undefined}
    >
      {showImage ? (
        <img
          key={headerImageUrl}
          src={headerImageUrl ?? undefined}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
      <div
        className={cn('absolute inset-x-0 bottom-0', scrimHeightClass)}
        style={{
          background: `linear-gradient(180deg, rgba(0,0,0,0), ${scrimTo})`,
        }}
        aria-hidden
      />
    </div>
  );
}

function MetaRow({
  identity,
  centered,
  compact,
  stackedMobile,
}: {
  identity: ProfileHeaderIdentity;
  centered?: boolean;
  compact?: boolean;
  /** Cards mobile: stack job/company/location; desktop wraps in a row. */
  stackedMobile?: boolean;
}) {
  const job = identity.jobTitle?.trim();
  const company = identity.company?.trim();
  const location = identity.location?.trim();

  if (compact) {
    const parts = [company, location].filter(Boolean);
    return (
      <>
        {job ? (
          <div
            className={cn(
              'mt-2 text-[14px] text-[var(--muted)]',
              centered && 'text-center',
            )}
          >
            {job}
          </div>
        ) : null}
        {parts.length > 0 ? (
          <div
            className={cn(
              'mt-1 text-[13px] text-[var(--faint)]',
              centered && 'text-center',
            )}
          >
            {parts.join(' · ')}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div
      className={cn(
        stackedMobile
          ? 'mt-[9px] flex flex-col items-center gap-[5px] @min-[880px]/profile:mt-2.5 @min-[880px]/profile:flex-row @min-[880px]/profile:flex-wrap @min-[880px]/profile:gap-x-[18px] @min-[880px]/profile:gap-y-1.5'
          : 'mt-2 flex flex-wrap gap-x-4 gap-y-1.5',
        centered || stackedMobile ? 'justify-center' : 'justify-start',
      )}
    >
      {job ? (
        <span className="flex items-center gap-[7px] text-[13.5px] text-[var(--muted)]">
          <Briefcase
            className="h-[15px] w-[15px] shrink-0"
            style={{ stroke: 'var(--accent)' }}
            strokeWidth={1.7}
          />
          {job}
        </span>
      ) : null}
      {company ? (
        <span className="flex items-center gap-[7px] text-[13px] text-[var(--faint)]">
          <Building2
            className="h-[14px] w-[14px] shrink-0"
            style={{ stroke: 'var(--faint)' }}
            strokeWidth={1.7}
          />
          {company}
        </span>
      ) : null}
      {location ? (
        <span className="flex items-center gap-[7px] text-[13px] text-[var(--faint)]">
          <MapPin
            className="h-[14px] w-[14px] shrink-0"
            style={{ stroke: 'var(--faint)' }}
            strokeWidth={1.7}
          />
          {location}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Profile header — image/gradient/minimal; text always below the banner (handover §4).
 */
export function ProfileHeader({
  layout,
  headerStyle,
  headerImageUrl,
  identity,
  resolveFileUrl,
  actions,
  className,
}: ProfileHeaderProps) {
  if (layout === 'centered') {
    return (
      <div className={cn('w-full text-center', className)}>
        <HeaderBanner
          headerStyle={headerStyle}
          headerImageUrl={headerImageUrl}
          heightClass="h-[150px] @min-[880px]/profile:h-[170px]"
          radiusClass="rounded-[22px] @min-[880px]/profile:rounded-[24px]"
          scrimTo="var(--bg)"
          scrimHeightClass="h-20 @min-[880px]/profile:h-[90px]"
          marginBottomClass="-mb-[62px] @min-[880px]/profile:-mb-[66px]"
        />
        <div className="relative flex flex-col items-center">
          <ProfileAvatar
            identity={identity}
            resolveFileUrl={resolveFileUrl}
            sizeClass="h-[118px] w-[118px] @min-[880px]/profile:h-[124px] @min-[880px]/profile:w-[124px]"
            borderClass="border-4 border-[var(--bg)]"
            fontClass="text-[38px] @min-[880px]/profile:text-[40px]"
            shadowClass="shadow-[0_16px_40px_var(--accent-16)]"
          />
          <h1 className="mt-[18px] text-[25px] font-bold tracking-[-0.4px] text-[var(--text)] @min-[880px]/profile:text-[27px] @min-[880px]/profile:tracking-[-0.5px]">
            {identity.displayName}
          </h1>
          <MetaRow identity={identity} centered compact />
        </div>
        {actions}
      </div>
    );
  }

  if (layout === 'classic') {
    // Single CTA in the flex flow (not duplicated) so layout is stable from
    // first paint: column on mobile (avatar → meta → CTAs), row on desktop.
    return (
      <div className={cn('w-full', className)}>
        <HeaderBanner
          headerStyle={headerStyle}
          headerImageUrl={headerImageUrl}
          heightClass="h-[116px] @min-[880px]/profile:h-[210px]"
          radiusClass="rounded-[18px] @min-[880px]/profile:rounded-[22px]"
          scrimTo="var(--bg)"
          scrimHeightClass="h-[70px] @min-[880px]/profile:h-[100px]"
          marginBottomClass="-mb-9 @min-[880px]/profile:-mb-[46px]"
        />
        <div className="relative flex flex-col gap-4 @min-[880px]/profile:flex-row @min-[880px]/profile:items-end @min-[880px]/profile:gap-[22px] @min-[880px]/profile:px-2">
          <div className="flex items-end gap-4">
            <ProfileAvatar
              identity={identity}
              resolveFileUrl={resolveFileUrl}
              sizeClass="h-[76px] w-[76px] @min-[880px]/profile:h-24 @min-[880px]/profile:w-24"
              shapeClass="rounded-[20px] @min-[880px]/profile:rounded-[24px]"
              borderClass="border-[3px] border-[var(--bg)] @min-[880px]/profile:border-4"
              fontClass="text-[25px] @min-[880px]/profile:text-[32px]"
              shadowClass=""
            />
            <div className="pb-1 @min-[880px]/profile:hidden">
              <h1 className="text-2xl font-bold leading-tight tracking-[-0.5px] text-[var(--text)]">
                {identity.displayName}
              </h1>
              {identity.jobTitle?.trim() ? (
                <div className="mt-1.5 text-[13px] text-[var(--muted)]">
                  {identity.jobTitle.trim()}
                </div>
              ) : null}
            </div>
          </div>
          <div className="hidden min-w-0 flex-1 pb-1.5 @min-[880px]/profile:block">
            <h1 className="text-[30px] font-bold tracking-[-0.6px] text-[var(--text)]">
              {identity.displayName}
            </h1>
            <MetaRow identity={identity} />
          </div>
          <div className="flex gap-2 text-[12.5px] text-[var(--faint)] @min-[880px]/profile:hidden">
            {identity.company?.trim() ? (
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" strokeWidth={1.7} />
                {identity.company.trim()}
              </span>
            ) : null}
            {identity.location?.trim() ? (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" strokeWidth={1.7} />
                {identity.location.trim()}
              </span>
            ) : null}
          </div>
          {actions ? (
            <div className="shrink-0 @min-[880px]/profile:pb-1.5">
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // cards
  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] @min-[880px]/profile:rounded-[18px]',
        className,
      )}
    >
      <HeaderBanner
        headerStyle={headerStyle}
        headerImageUrl={headerImageUrl}
        heightClass="h-[172px] @min-[880px]/profile:h-[210px]"
        scrimTo="var(--surface)"
        scrimHeightClass="h-[92px] @min-[880px]/profile:h-[110px]"
      />
      <div className="relative -mt-[52px] flex flex-col items-center px-4 pb-[22px] @min-[880px]/profile:-mt-14 @min-[880px]/profile:px-6 @min-[880px]/profile:pb-7">
        <ProfileAvatar
          identity={identity}
          resolveFileUrl={resolveFileUrl}
          sizeClass="h-[100px] w-[100px] @min-[880px]/profile:h-[112px] @min-[880px]/profile:w-[112px]"
          borderClass="border-4 border-[var(--avatar-border)]"
          fontClass="text-[32px] @min-[880px]/profile:text-4xl"
        />
        <h1 className="mt-[13px] text-[22px] font-bold tracking-[-0.4px] text-[var(--text)] @min-[880px]/profile:mt-3.5 @min-[880px]/profile:text-[26px] @min-[880px]/profile:tracking-[-0.5px]">
          {identity.displayName}
        </h1>
        <MetaRow identity={identity} centered stackedMobile />
        {actions}
      </div>
    </div>
  );
}
